import { gql, useApolloClient, useMutation } from '@apollo/client';
import type {
  Event,
  EventCategory,
  EventCategoryMapping,
  EventFeaturedUser,
  EventVenue,
  User,
} from '@shady-pines/db';
import { isNonEmptyArray, isNonNil, now, type Timestamp, type Uuid } from '@shady-pines/types';
import { head, isNil } from 'lodash';
import { useCallback } from 'react';
import { useSupabaseStorage } from '../storage/use-supabase-storage';
import type { MutationResponse } from '../types';
import { useSupabase } from '../use-supabase';
import {
  useCreateEventCategory,
  useCreateEventCategoryMappings,
  useDeleteEventCategoryMapping,
} from './use-event-categories';
import {
  useCreateEventFeaturedUsers,
  useDeleteEventFeaturedUsers,
} from './use-event-featured-users';

const CREATE_EVENT = gql`
  mutation CreateEvent(
    $title: String!
    $description: String!
    $startTime: Timestamp!
    $endTime: Timestamp!
    $venueId: UUID
    $createdBy: UUID!
    $active: Boolean!
    $recurring: Boolean!
    $featured: Boolean!
    $status: String!
  ) {
    insertIntoevent_eventsCollection(
      objects: [
        {
          title: $title
          description: $description
          start_time: $startTime
          end_time: $endTime
          venue_id: $venueId
          created_by: $createdBy
          active: $active
          recurring: $recurring
          featured: $featured
          status: $status
        }
      ]
    ) {
      records {
        __typename
        id
        title
        description
        start_time
        end_time
        venue_id
        created_by
        active
        recurring
        featured
        status
      }
    }
  }
`;

export const useCreateEvent = () => {
  const supabase = useSupabase();
  const [createEvent, { loading, error }] =
    useMutation<MutationResponse<'insertIntoevent_eventsCollection', Event>>(CREATE_EVENT);

  const create = useCallback(
    async (input: {
      title: string;
      description: string;
      startTime: Timestamp;
      endTime: Timestamp;
      venueId: Uuid<EventVenue> | null;
      active: boolean;
      recurring: boolean;
      featured: boolean;
      status: 'pending' | 'approved' | 'rejected';
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('User must be authenticated to create events');
      }

      return createEvent({
        variables: {
          title: input.title,
          description: input.description,
          startTime: input.startTime,
          endTime: input.endTime,
          venueId: input.venueId,
          active: input.active,
          recurring: input.recurring,
          featured: input.featured,
          status: input.status,
          createdBy: user.id,
        },
      });
    },
    [createEvent, supabase],
  );

  return {
    createEvent: create,
    loading,
    error,
  };
};

const EDIT_EVENT = gql`
  mutation EditEvent(
    $id: UUID!
    $title: String
    $description: String
    $startTime: Timestamp
    $endTime: Timestamp
    $venueId: UUID
    $active: Boolean
    $recurring: Boolean
    $deletedAt: Timestamp
    $status: String
    $featured: Boolean
    $rejectionReason: String
  ) {
    updateevent_eventsCollection(
      set: {
        title: $title
        description: $description
        start_time: $startTime
        end_time: $endTime
        venue_id: $venueId
        active: $active
        recurring: $recurring
        deleted_at: $deletedAt
        status: $status
        featured: $featured
        rejection_reason: $rejectionReason
      }
      filter: { id: { eq: $id } }
      atMost: 1
    ) {
      records {
        __typename
        id
        title
        description
        start_time
        end_time
        venue_id
        created_by
        active
        recurring
        status
        deleted_at
        featured
        rejection_reason
      }
    }
  }
`;

export const useEditEvent = () => {
  const [editEvent, { loading, error }] = useMutation<
    MutationResponse<'updateevent_eventsCollection', Event>
  >(EDIT_EVENT, {
    refetchQueries: ['GetEvents'],
  });

  const edit = useCallback(
    (
      id: Uuid<Event>,
      input: {
        title?: string;
        description?: string;
        startTime?: Timestamp;
        endTime?: Timestamp;
        venueId?: Uuid<EventVenue>;
        active?: boolean;
        recurring?: boolean;
        deletedAt?: Timestamp;
        status?: Event['status'];
        featured?: boolean;
        rejectionReason?: string | null;
      },
    ) => {
      return editEvent({
        variables: {
          id,
          title: input.title,
          description: input.description,
          startTime: input.startTime,
          endTime: input.endTime,
          venueId: input.venueId,
          active: input.active,
          recurring: input.recurring,
          deletedAt: input.deletedAt,
          status: input.status,
          featured: input.featured,
          rejectionReason: input.rejectionReason,
        },
      });
    },
    [editEvent],
  );

  return {
    editEvent: edit,
    loading,
    error,
  };
};

export const useEditEvents = () => {
  const { editEvent } = useEditEvent();

  const editEvents = useCallback(
    async (
      ids: Uuid<Event>[],
      input: {
        active?: boolean;
        status?: Event['status'];
        deletedAt?: Timestamp;
        rejectionReason?: string | null;
      },
    ) => {
      const promises = ids.map((id) =>
        editEvent(id, {
          active: input.active,
          status: input.status,
          deletedAt: input.deletedAt,
          rejectionReason: input.rejectionReason,
        }),
      );

      return Promise.all(promises);
    },
    [editEvent],
  );

  return {
    editEvents,
  };
};

export const useManageEvent = () => {
  const client = useApolloClient();
  const edit = useEditEvent();
  const create = useCreateEvent();
  const deleteEvent = useCallback(
    async (id: Uuid<Event>) => {
      await edit.editEvent(id, {
        deletedAt: now(),
      });
    },
    [edit],
  );
  const createFeaturedUsers = useCreateEventFeaturedUsers();
  const deleteFeaturedUsers = useDeleteEventFeaturedUsers();
  const categories = useCreateEventCategory();
  const createCategoryMappings = useCreateEventCategoryMappings();
  const deleteCategoryMappings = useDeleteEventCategoryMapping();
  const { uploadFile, isUploading } = useSupabaseStorage();

  const handleSaveImage = useCallback(
    async (eventId: Uuid<Event>, file: File) => {
      try {
        const buffer = await file.arrayBuffer();
        await uploadFile({
          bucket: 'event-artwork',
          fileName: eventId,
          file: buffer,
          fileType: file.type,
        });
      } catch (error) {
        console.error('Failed to update event image:', error);
      }
    },
    [uploadFile],
  );

  const createFullEvent = useCallback(
    async (
      event: Omit<Event, 'id' | 'created_by' | 'deleted_at' | 'created_at' | 'updated_at'>,
      artwork: File | null,
      categoryIds: Uuid<EventCategory>[],
      featuredUserIds: Uuid<User>[],
    ) => {
      const eventResponse = await create.createEvent({
        ...event,
        startTime: event.start_time,
        endTime: event.end_time,
        venueId: event.venue_id,
      });

      const createdEvent = head(eventResponse.data?.insertIntoevent_eventsCollection.records);
      if (isNil(createdEvent)) {
        throw new Error('Failed to create event');
      }

      if (isNonNil(artwork)) {
        await handleSaveImage(createdEvent.id, artwork);
      }

      if (isNonEmptyArray(categoryIds)) {
        await createCategoryMappings.createEventCategoryMappings({
          eventId: createdEvent.id,
          categoryIds,
        });
      }

      if (isNonEmptyArray(featuredUserIds)) {
        await createFeaturedUsers.createEventFeaturedUsers({
          eventId: createdEvent.id,
          userIds: featuredUserIds,
        });
      }

      await client.refetchQueries({
        include: ['GetEvents', 'GetMyEvents', 'GetEventCategoryMappings', 'GetEventFeaturedUsers'],
      });

      return createdEvent;
    },

    [client, create, createCategoryMappings, createFeaturedUsers, handleSaveImage],
  );

  const updateFullEvent = useCallback(
    async (
      eventId: Uuid<Event>,
      update: {
        event: Partial<Event> & {};
        artwork: File | null;
        categories: {
          add: Uuid<EventCategory>[];
          remove: Uuid<EventCategoryMapping>[];
        };
        featuredUsers: {
          add: Uuid<User>[];
          remove: Uuid<EventFeaturedUser>[];
        };
      },
    ) => {
      await edit.editEvent(eventId, update.event);
      if (isNonNil(update.artwork)) {
        await handleSaveImage(eventId, update.artwork);
      }
      if (isNonEmptyArray(update.categories.add)) {
        await createCategoryMappings.createEventCategoryMappings({
          eventId,
          categoryIds: update.categories.add,
        });
      }
      if (isNonEmptyArray(update.categories.remove)) {
        await deleteCategoryMappings.deleteEventCategoryMappings(update.categories.remove);
      }
      if (isNonEmptyArray(update.featuredUsers.add)) {
        await createFeaturedUsers.createEventFeaturedUsers({
          eventId,
          userIds: update.featuredUsers.add,
        });
      }
      if (isNonEmptyArray(update.featuredUsers.remove)) {
        await deleteFeaturedUsers.deleteEventFeaturedUsers(update.featuredUsers.remove);
      }

      await client.refetchQueries({
        include: ['GetEvents', 'GetMyEvents', 'GetEventCategoryMappings', 'GetEventFeaturedUsers'],
      });
    },
    [
      client,
      createCategoryMappings,
      createFeaturedUsers,
      deleteCategoryMappings,
      deleteFeaturedUsers,
      edit,
      handleSaveImage,
    ],
  );

  return {
    fullEvent: {
      create: {
        create: createFullEvent,
        errors: [create.error, createCategoryMappings.error, createFeaturedUsers.error].filter(
          isNonNil,
        ),
        loading:
          create.loading ||
          createCategoryMappings.loading ||
          createFeaturedUsers.loading ||
          isUploading,
      },
      update: {
        update: updateFullEvent,
        errors: [edit.error, createCategoryMappings.error, createFeaturedUsers.error].filter(
          isNonNil,
        ),
        loading:
          edit.loading ||
          createCategoryMappings.loading ||
          createFeaturedUsers.loading ||
          deleteCategoryMappings.loading ||
          deleteFeaturedUsers.loading ||
          isUploading,
      },
    },
    events: {
      edit,
      create,
      remove: deleteEvent,
    },
    featuredUsers: {
      create: createFeaturedUsers,
      remove: deleteFeaturedUsers,
    },
    categories: {
      create: categories,
      createMappings: createCategoryMappings,
      deleteMappings: deleteCategoryMappings,
    },
  };
};
